import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import api from '../../lib/api';
import { track } from '../../lib/analytics';
import { CheckoutColumns } from './CheckoutLayout';
import { PaymentPanel, MobilePayBar } from './PaymentPanel';
import { CouponDrawer } from './CouponDrawer';
import { CouponAppliedModal } from './CouponAppliedModal';

const toQuote = (q) => ({
  currency: q.currency, divisor: q.divisor || 100, subtotal: q.subtotal_minor, discount: q.discount_minor,
  total: q.total_minor, tax: 0, code: q.applied_code || null, percentOff: q.percent_off || null,
});

/**
 * Fallback mode (no publishable key / Stripe direct API unavailable): same
 * branded page, but "Pay" hands off to Stripe's hosted checkout with the
 * chosen coupon pre-applied so the strikethrough carries over.
 */
export default function HostedFallback({ cfg, urlCode, email, reason }) {
  const [quote, setQuote] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [applied, setApplied] = useState(null);
  const [couponErr, setCouponErr] = useState('');
  const [busyCode, setBusyCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState('');
  const autoRef = useRef(false);

  const fetchQuote = useCallback(async (code, { fromUrl } = {}) => {
    const c = String(code || '').trim().toUpperCase();
    if (c) setBusyCode(c);
    setCouponErr('');
    try {
      const { data } = await api.get('/api/checkout/quote', { params: { code: c, plan: cfg.plan === 'trial' ? 'trial' : 'pro' } });
      const q = toQuote(data);
      setQuote(q);
      if (!c) setApplied(null);
      if (c) {
        setApplied({ code: q.code, savings: q.discount, total: q.total, currency: q.currency, divisor: q.divisor });
        setDrawerOpen(false);
        track('coupon_applied', { code: c, savings: q.discount / q.divisor, value: q.total / q.divisor, currency: q.currency, mode: 'hosted' });
      } else {
        track('checkout_ready', { mode: 'hosted', reason: reason || '', value: q.total / q.divisor, currency: q.currency });
      }
      return true;
    } catch (e) {
      if (c) {
        setCouponErr(e.response?.data?.detail || cfg.coupon_invalid_text || "That code isn't valid or has expired.");
        if (fromUrl) setDrawerOpen(true);
        track('coupon_failed', { code: c });
        if (!quote) fetchQuote('');
      } else {
        setPayErr('Could not load pricing. Please refresh the page.');
      }
      return false;
    } finally {
      setBusyCode('');
    }
  }, [quote, reason, cfg.coupon_invalid_text, cfg.plan]);

  useEffect(() => {
    if (autoRef.current) return;
    autoRef.current = true;
    const autoCode = urlCode || (cfg.credit?.active ? cfg.credit.code : '');
    fetchQuote(autoCode || '', { fromUrl: !!urlCode });
  }, [fetchQuote, urlCode, cfg.credit]);

  const onConsent = (v) => { setConsent(v); setPayErr(''); api.post('/api/payments/checkout-consent', { accepted: v }).catch(() => {}); };

  const pay = async () => {
    if (!consent) { setPayErr(cfg.consent_required_text || 'Please tick the refund policy box to continue.'); return; }
    setPaying(true); setPayErr('');
    track('checkout_pay_click', { value: quote ? quote.total / quote.divisor : 0, currency: quote?.currency, code: quote?.code || '', mode: 'hosted' });
    track('add_payment_info', { value: quote ? quote.total / quote.divisor : 0, currency: quote?.currency, coupon: quote?.code || '' });
    try {
      const { data } = await api.post('/api/payments/create-checkout', { plan_id: cfg.plan === 'trial' ? 'trial' : 'pro', origin_url: window.location.origin, promo_code: quote?.code || '' });
      if (data?.url) { window.location.href = data.url; return; }
      setPayErr('Could not start the payment. Please try again.');
    } catch (e) {
      setPayErr(e.response?.data?.detail || 'Could not start the payment. Please try again.');
    }
    setPaying(false);
  };

  return (
    <>
      <CheckoutColumns
        cfg={cfg}
        quote={quote}
        summary={{ onOpenCoupons: () => setDrawerOpen(true), onApplyCode: (c) => fetchQuote(c), onRemoveCode: () => fetchQuote(''), busyCode }}
        payment={(
          <PaymentPanel cfg={cfg} quote={quote} email={email} consent={consent} onConsent={onConsent} onPay={pay} paying={paying} payError={payErr}
            canPay={!!quote} ctaLabel={quote ? (cfg.hosted_cta_label || 'Continue to secure payment') : ''} ctaHint={cfg.hosted_cta_hint}>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3.5 sm:p-4 flex gap-3" data-testid="hosted-fallback-notice">
              <ShieldCheck size={20} className="text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-slate-600 leading-relaxed">
                <span className="sm:hidden" data-testid="hosted-notice-mobile">{cfg.hosted_notice_mobile || "You'll enter your card on Stripe's secure page next."}</span>
                <span className="hidden sm:inline" data-testid="hosted-notice">{cfg.hosted_notice || "Card details are collected on Stripe's PCI-certified page in the next step. We never see or store your card number."}
                <span className="inline-flex items-center gap-1 text-xs text-slate-400 ml-1"><ExternalLink size={11} />stripe.com</span></span>
              </div>
            </div>
          </PaymentPanel>
        )}
      />
      <CouponDrawer open={drawerOpen} cfg={cfg} onClose={() => setDrawerOpen(false)} onApply={(c) => fetchQuote(c)} busyCode={busyCode} error={couponErr} appliedCode={quote?.code} />
      <CouponAppliedModal cfg={cfg} data={applied} onClose={() => setApplied(null)} />
      <MobilePayBar cfg={cfg} quote={quote} onPay={pay} paying={paying} disabled={false} consent={consent} onConsent={onConsent} ctaLabel={cfg.hosted_cta_label || 'Continue to secure payment'} />
    </>
  );
}
